/**
 * SessionPollsCard — iter258. Lightweight scheduling polls on the Sessions tab.
 * Any member can create a poll ("Sat 7am or Sun 8am?"), everyone votes,
 * creator/admin closes → winner highlighted.
 */
import { useState, useEffect, useCallback } from 'react';
import { Vote, Plus, X, Trophy, Lock, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import api from '../lib/api';

export default function SessionPollsCard({ groupId, isAdmin, myUserId }) {
  const [polls, setPolls] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState(['', '']);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!groupId) return;
    api.get(`/groups/${groupId}/polls`).then((res) => setPolls(res.data?.polls || [])).catch(() => {});
  }, [groupId]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    const opts = options.map((o) => o.trim()).filter(Boolean);
    if (!question.trim()) { toast.error('Add a question'); return; }
    if (opts.length < 2) { toast.error('Add at least 2 options'); return; }
    setBusy(true);
    try {
      await api.post(`/groups/${groupId}/polls`, { question: question.trim(), options: opts });
      toast.success('Poll created — members notified');
      setShowCreate(false); setQuestion(''); setOptions(['', '']);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create poll');
    } finally { setBusy(false); }
  };

  const handleVote = async (pollId, idx) => {
    try {
      const res = await api.post(`/polls/${pollId}/vote`, { option_index: idx });
      setPolls((prev) => prev.map((p) => (p.id === pollId ? res.data : p)));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Vote failed');
    }
  };

  const handleClose = async (pollId) => {
    try {
      const res = await api.post(`/polls/${pollId}/close`);
      setPolls((prev) => prev.map((p) => (p.id === pollId ? res.data : p)));
      toast.success('Poll closed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to close');
    }
  };

  const handleDelete = async (pollId) => {
    if (!window.confirm('Delete this poll?')) return;
    try {
      await api.delete(`/polls/${pollId}`);
      setPolls((prev) => prev.filter((p) => p.id !== pollId));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const openPolls = polls.filter((p) => p.status === 'open');
  const recentClosed = polls.filter((p) => p.status === 'closed').slice(0, 2);
  const visible = [...openPolls, ...recentClosed];

  if (visible.length === 0 && !showCreate) {
    return (
      <button
        type="button"
        onClick={() => setShowCreate(true)}
        data-testid="new-poll-btn"
        className="w-full flex items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-border py-2.5 text-xs font-bold text-muted-foreground hover:text-foreground hover:border-primary/40 transition"
      >
        <Vote className="w-3.5 h-3.5" /> Ask the group — start a quick poll
      </button>
    );
  }

  return (
    <div className="space-y-2" data-testid="session-polls-card">
      {showCreate ? (
        <Card className="p-4 border-2 rounded-2xl space-y-2" data-testid="poll-create-form">
          <div className="flex items-center justify-between">
            <p className="text-sm font-bold flex items-center gap-1.5"><Vote className="w-4 h-4 text-primary" /> New poll</p>
            <button onClick={() => setShowCreate(false)} data-testid="poll-create-cancel"><X className="w-4 h-4 text-muted-foreground" /></button>
          </div>
          <Input
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="e.g. Which slot this weekend?"
            data-testid="poll-question-input"
            className="h-10 border-2 rounded-xl"
          />
          {options.map((opt, i) => (
            <div key={i} className="flex gap-2">
              <Input
                value={opt}
                onChange={(e) => setOptions((prev) => prev.map((o, j) => (j === i ? e.target.value : o)))}
                placeholder={`Option ${i + 1} — e.g. Sat 7 AM · Court 3`}
                data-testid={`poll-option-input-${i}`}
                className="h-9 border-2 rounded-xl text-sm"
              />
              {options.length > 2 && (
                <button onClick={() => setOptions((prev) => prev.filter((_, j) => j !== i))} data-testid={`poll-option-remove-${i}`}>
                  <X className="w-4 h-4 text-muted-foreground" />
                </button>
              )}
            </div>
          ))}
          <div className="flex items-center justify-between pt-1">
            {options.length < 6 ? (
              <button
                type="button"
                onClick={() => setOptions((prev) => [...prev, ''])}
                data-testid="poll-add-option-btn"
                className="text-xs font-bold text-primary flex items-center gap-1"
              >
                <Plus className="w-3 h-3" /> Add option
              </button>
            ) : <span />}
            <Button size="sm" onClick={handleCreate} disabled={busy} data-testid="poll-create-submit" className="rounded-full text-xs">
              {busy ? 'Creating…' : 'Start poll'}
            </Button>
          </div>
        </Card>
      ) : (
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          data-testid="new-poll-btn"
          className="w-full flex items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-border py-2 text-xs font-bold text-muted-foreground hover:text-foreground hover:border-primary/40 transition"
        >
          <Plus className="w-3 h-3" /> New poll
        </button>
      )}

      {visible.map((poll) => {
        const canManage = isAdmin || poll.created_by === myUserId;
        const maxCount = Math.max(1, ...poll.options.map((o) => o.count));
        return (
          <Card key={poll.id} className="p-4 border-2 rounded-2xl" data-testid={`poll-${poll.id}`}>
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="min-w-0">
                <p className="text-sm font-bold leading-snug">{poll.question}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  by {poll.created_by_name} · {poll.total_votes} vote{poll.total_votes === 1 ? '' : 's'}
                  {poll.status === 'closed' && ' · closed'}
                </p>
              </div>
              {canManage && (
                <div className="flex gap-1 shrink-0">
                  {poll.status === 'open' && (
                    <button onClick={() => handleClose(poll.id)} title="Close poll" data-testid={`poll-close-${poll.id}`} className="p-1.5 rounded-full hover:bg-accent">
                      <Lock className="w-3.5 h-3.5 text-muted-foreground" />
                    </button>
                  )}
                  <button onClick={() => handleDelete(poll.id)} title="Delete poll" data-testid={`poll-delete-${poll.id}`} className="p-1.5 rounded-full hover:bg-accent">
                    <Trash2 className="w-3.5 h-3.5 text-muted-foreground" />
                  </button>
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              {poll.options.map((opt, i) => {
                const pct = poll.total_votes > 0 ? Math.round((opt.count / poll.total_votes) * 100) : 0;
                const isMine = poll.my_vote === i;
                const isWinner = poll.status === 'closed' && poll.winner_index === i;
                return (
                  <button
                    key={i}
                    type="button"
                    disabled={poll.status !== 'open'}
                    onClick={() => handleVote(poll.id, i)}
                    data-testid={`poll-vote-${poll.id}-${i}`}
                    title={opt.voter_names?.join(', ') || 'No votes yet'}
                    className={`relative w-full overflow-hidden rounded-xl border-2 px-3 py-2 text-left transition ${
                      isWinner ? 'border-emerald-500/60 bg-emerald-500/5'
                        : isMine ? 'border-primary/60 bg-primary/5'
                        : 'border-border hover:border-primary/40'
                    } ${poll.status !== 'open' ? 'cursor-default' : ''}`}
                  >
                    <div
                      className={`absolute inset-y-0 left-0 ${isWinner ? 'bg-emerald-500/15' : 'bg-primary/10'}`}
                      style={{ width: `${opt.count > 0 ? Math.max(8, (opt.count / maxCount) * 100) : 0}%` }}
                    />
                    <div className="relative flex items-center justify-between gap-2">
                      <span className="text-xs font-semibold truncate">
                        {isWinner && <Trophy className="w-3 h-3 inline mr-1 text-emerald-600" />}
                        {opt.label}
                        {isMine && <span className="ml-1.5 text-primary">✓</span>}
                      </span>
                      <span className="text-xs font-bold tabular-nums text-muted-foreground shrink-0">
                        {opt.count} · {pct}%
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </Card>
        );
      })}
    </div>
  );
}
